import * as Sentry from '@sentry/react';

const isError = e => e instanceof Error;

const RESERVED_ERROR_KEYS = new Set([
  'cause',
  'contexts',
  'error',
  'extra',
  'extras',
  'fingerprint',
  'level',
  'message',
  'name',
  'networkContext',
  'originalError',
  'pageContext',
  'requestContext',
  'stack',
  'tags',
  'user',
]);

function getPageContext() {
  if (typeof window === 'undefined') {
    return {};
  }

  return {
    currentUrl: window.location.href,
    inIframe: window.self !== window.top,
    origin: window.location.origin,
    pathname: window.location.pathname,
    referrer: typeof document !== 'undefined' ? document.referrer || null : null,
    visibilityState: typeof document !== 'undefined' ? document.visibilityState : null,
  };
}

function getNetworkContext() {
  if (typeof navigator === 'undefined') {
    return {};
  }

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  return {
    connection: connection
      ? {
          downlink: connection.downlink,
          effectiveType: connection.effectiveType,
          rtt: connection.rtt,
          saveData: connection.saveData,
          type: connection.type,
        }
      : null,
    language: navigator.language,
    onLine: navigator.onLine,
    userAgent: navigator.userAgent,
  };
}

function toSerializable(value, seen = new WeakSet()) {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  if (typeof value === 'function') {
    return `[Function ${value.name || 'anonymous'}]`;
  }

  if (value instanceof Error) {
    return {
      message: value.message,
      name: value.name,
      stack: value.stack,
    };
  }

  if (Array.isArray(value)) {
    return value.map(item => toSerializable(item, seen));
  }

  if (typeof value === 'object') {
    if (seen.has(value)) {
      return '[Circular]';
    }

    seen.add(value);

    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, toSerializable(item, seen)]),
    );
  }

  return String(value);
}

function normalizeError(err) {
  if (isError(err)) {
    return err;
  }

  if (typeof err === 'string') {
    return new Error(err);
  }

  if (err && typeof err === 'object') {
    if (isError(err.error)) {
      return err.error;
    }

    return new Error(err.message || JSON.stringify(toSerializable(err)));
  }

  return new Error(String(err));
}

function buildScopeData(sourceError, error) {
  const pageContext = error.pageContext || sourceError?.pageContext || getPageContext();
  const networkContext = error.networkContext || sourceError?.networkContext || getNetworkContext();
  const contexts = {
    ...(error.contexts || sourceError?.contexts || {}),
    ...(error.requestContext && { request: error.requestContext }),
    ...(networkContext && Object.keys(networkContext).length > 0 && { network: networkContext }),
    ...(pageContext && Object.keys(pageContext).length > 0 && { page: pageContext }),
  };

  const extras = {
    ...(error.extra || error.extras || sourceError?.extra || sourceError?.extras || {}),
  };

  const tags = {
    ...(error.tags || sourceError?.tags || {}),
  };

  if (error.url) {
    extras.url = error.url;
  }

  if (error.originalError) {
    extras.originalError = toSerializable(error.originalError);
  }

  if (error.method) {
    tags.method = error.method;
  }

  if (error.name) {
    tags.error_name = error.name;
  }

  if (sourceError && typeof sourceError === 'object' && !isError(sourceError)) {
    for (const [key, value] of Object.entries(sourceError)) {
      if (!RESERVED_ERROR_KEYS.has(key)) {
        extras[key] = toSerializable(value);
      }
    }
  }

  return {
    contexts,
    extras,
    fingerprint: error.fingerprint || sourceError?.fingerprint,
    level: error.level || sourceError?.level,
    tags,
    user: error.user || sourceError?.user,
  };
}

const logException = err => {
  const error = normalizeError(err);
  const scopeData = buildScopeData(err, error);

  Sentry.withScope(scope => {
    Object.entries(scopeData.tags).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        scope.setTag(key, String(value));
      }
    });

    Object.entries(scopeData.contexts).forEach(([key, value]) => {
      if (value && Object.keys(value).length > 0) {
        scope.setContext(key, toSerializable(value));
      }
    });

    Object.entries(scopeData.extras).forEach(([key, value]) => {
      if (value !== undefined) {
        scope.setExtra(key, toSerializable(value));
      }
    });

    if (scopeData.user) {
      scope.setUser(toSerializable(scopeData.user));
    }

    if (scopeData.level) {
      scope.setLevel(scopeData.level);
    }

    if (scopeData.fingerprint) {
      scope.setFingerprint(scopeData.fingerprint);
    }

    Sentry.captureException(error);
  });

  if (process.env.NODE_ENV === 'development') {
    if (typeof window !== 'undefined') {
      window.console && console.error && console.log(error);
    }
  }
};

export default logException;
